// AI Agent SSE client
// Streams a chat turn from POST /api/agent/chat. We use fetch + a ReadableStream
// reader (not EventSource) because we need to send a POST body and an auth header.
import { supabase } from './supabase';

const API_URL = import.meta.env.VITE_API_URL === '' ? '' : (import.meta.env.VITE_API_URL || 'http://localhost:3001');

export type AgentRole = 'user' | 'assistant';

export interface AgentChatMessage {
  role: AgentRole;
  content: string;
}

export interface AgentEntityRoute {
  detail?: string;
  list: string;
  idField: string;
}

// Discriminated union of server-sent events.
export type AgentEvent =
  | { type: 'token'; text: string }
  | { type: 'html'; html: string }
  | { type: 'tool_start'; sql: string }
  | {
      type: 'tool_result';
      entity: string | null;
      rows: any[];
      columns: string[];
      rowCount: number;
      sql: string;
      route: AgentEntityRoute | null;
    }
  | { type: 'tool_error'; error: string; sql: string }
  | {
      type: 'draft';
      entity: string;
      action: 'insert' | 'update';
      id: string | null;
      fields: Record<string, any>;
      summary: string | null;
      route: AgentEntityRoute | null;
    }
  | { type: 'error'; error: string }
  | { type: 'done' };

/**
 * Send recorded audio to the backend for transcription and return the text.
 * The audio is forwarded to the STT provider and never stored.
 */
export const transcribeAudio = async (
  blob: Blob,
  language = 'ar',
  signal?: AbortSignal,
  timeoutMs = 45_000,
): Promise<string> => {
  const token = await getAuthToken();
  // Always bound the request: without a timeout a hung proxy/backend would leave
  // the composer stuck on "transcribing…" forever with no way out.
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  const onOuterAbort = () => ctrl.abort();
  signal?.addEventListener('abort', onOuterAbort);

  let res: Response;
  try {
    res = await fetch(`${API_URL}/api/agent/transcribe?language=${encodeURIComponent(language)}`, {
      method: 'POST',
      headers: {
        'Content-Type': blob.type || 'audio/webm',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: blob,
      signal: ctrl.signal,
    });
  } catch (err: any) {
    if (err?.name === 'AbortError' && !signal?.aborted) {
      throw new Error('TRANSCRIBE_TIMEOUT');
    }
    throw err;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onOuterAbort);
  }

  let json: any = null;
  try { json = await res.json(); } catch { /* non-JSON error body */ }
  if (!res.ok || !json?.success) {
    throw new Error(json?.error || `Transcription failed (${res.status})`);
  }
  return String(json.text || '').trim();
};

/**
 * Upload a file the admin attached in the chat to the public agent-uploads
 * bucket, and return its public URL so the agent can reference it in a draft
 * (e.g. as a news cover image or a document attachment). Admin JWT only.
 */
export const uploadChatAttachment = async (name: string, dataUrl: string): Promise<string> => {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  const safe = name.replace(/[^a-zA-Z0-9._-]/g, '_') || 'file';
  const path = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}_${safe}`;
  const { error } = await supabase.storage
    .from('agent-uploads')
    .upload(path, blob, { upsert: true, contentType: blob.type || undefined, cacheControl: '3600' });
  if (error) throw error;
  return supabase.storage.from('agent-uploads').getPublicUrl(path).data.publicUrl;
};

/**
 * Apply an agent-proposed change through the admin-only RPC. Tags the write as
 * source='agent' so it's attributed correctly in the audit log. Admin JWT only.
 */
export const applyAgentChange = async (
  table: string,
  action: 'insert' | 'update' | 'delete',
  id: string | null,
  data: Record<string, any>,
): Promise<any> => {
  const { data: row, error } = await supabase.rpc('admin_apply_change', {
    p_table: table,
    p_action: action,
    p_id: id,
    p_data: data,
  });
  if (error) throw error;
  return row;
};

const getAuthToken = async (): Promise<string | null> => {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || null;
};

/**
 * Send the conversation to the agent and invoke onEvent for each streamed event.
 * Pass an AbortSignal to cancel mid-stream.
 */
export const streamAgentChat = async (
  messages: AgentChatMessage[],
  onEvent: (event: AgentEvent) => void,
  signal?: AbortSignal,
): Promise<void> => {
  const token = await getAuthToken();

  const response = await fetch(`${API_URL}/api/agent/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ messages }),
    signal,
  });

  if (!response.ok || !response.body) {
    // Non-streaming error response (e.g. 503 not configured, 400 bad request).
    let message = `Request failed (${response.status})`;
    try {
      const json = await response.json();
      message = json.error || json.message || message;
    } catch {
      /* ignore */
    }
    onEvent({ type: 'error', error: message });
    onEvent({ type: 'done' });
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const frames = buffer.split('\n\n');
    buffer = frames.pop() || ''; // keep partial frame

    for (const frame of frames) {
      const line = frame.trim();
      if (!line.startsWith('data:')) continue;
      const data = line.slice(5).trim();
      if (!data) continue;
      try {
        onEvent(JSON.parse(data) as AgentEvent);
      } catch {
        /* skip malformed frame */
      }
    }
  }
};
